import { useState, type ComponentProps, type ReactNode, type SyntheticEvent } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  BowlIcon,
  StorefrontIcon,
  TruckIcon,
  WalletIcon,
} from '../../components/icons/glyphs'
import {
  BakeryIllustration,
  CakeIllustration,
  ChefIllustration,
  GrillStationIllustration,
  GrillSuyaIllustration,
  JollofRiceIllustration,
  PastryBoxIllustration,
  SoupPotIllustration,
} from '../../components/icons/illustrations'
import { categoryImage, chefPhoto, isSyntheticSeedAsset } from './dishImages'
import { allowSeedContent, mediaUrl } from '../../lib/runtime'

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Centre the crop. object-fit: cover already defaults to 50% 50%, but
     stating it keeps the rule beside the one it depends on: these are food
     photographs shot with the dish centred, so a crop that drifts to an edge
     loses the subject. */
  object-position: center;
  display: block;
  /* Paint something while the bytes are in flight. Without it a slow connection
     shows a transparent hole where the food should be, which reads as a broken
     card rather than as a loading one. */
  background: ${minimalVars.color.bgSurfaceAlt};
`

const ChefThumbImg = styled(ThumbImg)`
  /* Portraits and headshots shot in 9:16 or 4:5 keep the subject's face
     in the centre. Centering avoids cutting off faces when cropped to
     square avatars or landscape cards. */
  object-position: center;
`

/*
 * A photo whose shape is far from its slot — a phone screenshot in a 4:3
 * card, a banner in a square avatar — loses most of itself to a cover crop.
 * Live uploads are mostly 3:4 phone shots (cover-cropped into 4:3 that is a
 * 1.78 mismatch, and the centred dish survives it), but 9:16 screenshots,
 * flyers and wide banners lost the dish or the text. Past this mismatch the
 * whole photo is shown over a blurred, cover-cropped copy of itself, so the
 * slot is still filled edge to edge and nothing is cut away.
 */
const LETTERBOX_MISMATCH = 2.05
/** Below this slot width a letterboxed photo is too small to read; crop instead. */
const LETTERBOX_MIN_SLOT = 96

const LetterboxFrame = styled.span`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  isolation: isolate;
`

const LetterboxBackdrop = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Scaled past the frame so the blur's soft edge never shows. */
  transform: scale(1.15);
  filter: blur(16px) saturate(1.1) brightness(0.92);
`

const SizedBox = styled.span<{ $size: number }>`
  flex: 0 0 ${({ $size }) => `${$size}px`};
  width: ${({ $size }) => `${$size}px`};
  height: ${({ $size }) => `${$size}px`};
  display: grid;
  place-items: stretch;
  overflow: hidden;
  border-radius: ${minimalVars.radius.md};
`

const GlyphTile = styled.span`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: ${minimalVars.color.brand};
  background: ${minimalVars.color.bgSurfaceAlt};

  /* An illustration is artwork for the whole slot, so it is stretched to the
     box and centred by its own preserveAspectRatio="slice" — matching the
     cover-filled photograph it stands in for. Left at its intrinsic size it
     sat centred in a larger tile with the background showing around it, which
     is the "images don't centre" the cards were showing. A small glyph icon
     keeps its own size: blowing a 24px line icon up to fill a card would look
     like a rendering fault, not artwork. */
  & > svg:not([width$='px']) {
    width: 100%;
    height: 100%;
    display: block;
  }
`

const getDishIllustration = (category?: string, name?: string): ReactNode => {
  const norm = `${category ?? ''} ${name ?? ''}`.toLowerCase()
  if (norm.includes('soup') || norm.includes('egusi') || norm.includes('efo') || norm.includes('stew') || norm.includes('soups')) {
    return <SoupPotIllustration size="100%" />
  }
  if (norm.includes('cake') || norm.includes('velvet') || norm.includes('sweet') || norm.includes('dessert')) {
    return <CakeIllustration size="100%" />
  }
  if (norm.includes('pastry') || norm.includes('pie') || norm.includes('roll') || norm.includes('box') || norm.includes('bakery') || norm.includes('bread')) {
    return <PastryBoxIllustration size="100%" />
  }
  if (norm.includes('suya') || norm.includes('grill') || norm.includes('snapper') || norm.includes('tilapia') || norm.includes('protein') || norm.includes('fish') || norm.includes('meat')) {
    return <GrillSuyaIllustration size="100%" />
  }
  return <JollofRiceIllustration size="100%" />
}

const getChefIllustration = (category?: string, name?: string): ReactNode => {
  const norm = `${category ?? ''} ${name ?? ''}`.toLowerCase()
  if (norm.includes('bakery') || norm.includes('patisserie') || norm.includes('pastr') || norm.includes('laila')) {
    return <BakeryIllustration size="100%" />
  }
  if (norm.includes('grill') || norm.includes('suya') || norm.includes('bbq') || norm.includes('tunde')) {
    return <GrillStationIllustration size="100%" />
  }
  return <ChefIllustration size="100%" />
}

const resolveEmojiGlyph = (emoji: unknown): ReactNode => {
  if (typeof emoji !== 'string') return null
  if (['👨🏿‍🍳', '👩🏾‍🍳', '👨🏽‍🍳', '👩🏼‍🍳', '👨‍🍳', '👩‍🍳', '🧑‍🍳'].includes(emoji)) return <ChefIllustration size="100%" />
  if (['🧁', '🎂', '🍰'].includes(emoji)) return <CakeIllustration size="100%" />
  if (['🥖', '🍞', '🥐', '🥯'].includes(emoji)) return <PastryBoxIllustration size="100%" />
  if (['🍛', '🍚', '🍗'].includes(emoji)) return <JollofRiceIllustration size="100%" />
  if (['🍲', '🥘', '🥣'].includes(emoji)) return <SoupPotIllustration size="100%" />
  if (['🍢', '🥩', '🥓', '🍖', '🧆'].includes(emoji)) return <GrillSuyaIllustration size="100%" />
  if (['🏪', '🏬', '🏢', '🛒'].includes(emoji)) return <StorefrontIcon size={24} />
  if (['🚚'].includes(emoji)) return <TruckIcon size={24} />
  if (['💸', '📥', '🏦', '💼'].includes(emoji)) return <WalletIcon size={24} />
  return null
}

const DERIVATIVE_WIDTHS = [128, 384, 768] as const

/**
 * The smallest server derivative covering `px` device pixels. Derivatives are
 * sized by their short edge, so this holds for any crop of the slot.
 */
const derivativeFor = (px: number): 128 | 384 | 768 =>
  DERIVATIVE_WIDTHS.find((width) => width >= px) ?? 768

/**
 * Renders high-fidelity culinary artwork, real photos, or rich SVG illustrations.
 */
export const ChowThumb = ({
  src,
  emoji,
  alt = '',
  category,
  name,
  size,
  thumbnailWidth,
  isChef = false,
  ratio,
  objectPosition,
}: {
  src?: string
  emoji?: ReactNode
  alt?: string
  category?: string
  name?: string
  /** Render inside a fixed square of this many px. Use when the host slot is unsized. */
  size?: number
  /** Explicit thumbnail derivative width without locking CSS container dimensions. */
  thumbnailWidth?: 128 | 384 | 768
  isChef?: boolean
  /**
   * Intrinsic aspect of the slot, as `[width, height]`.
   *
   * These are hints for the browser's layout, not a rendered size — the CSS
   * above still stretches the image to fill its slot. Without them the browser
   * cannot reserve the box before the bytes arrive, so every card jumps when
   * the photo lands: the layout shift that both loses the reader's place and
   * costs the page its Core Web Vitals score.
   */
  ratio?: readonly [number, number]
  /** Custom object-position for fine-tuned photo framing (e.g. "center 25%"). */
  objectPosition?: string
}) => {
  /* Load failures belong to one source. They are keyed by it and read back
     only while it is still the source, so a recycled card never shows the
     previous photo's fallback. Resetting them in an effect did exactly that
     for one frame, after the new source had already rendered. */
  const [failures, setFailures] = useState({ src, variant: false, source: false, fallback: false })
  const failed = failures.src === src ? failures : { src, variant: false, source: false, fallback: false }
  const fail = (key: 'variant' | 'source' | 'fallback') => setFailures({ ...failed, [key]: true })
  const variantFailed = failed.variant
  const srcFailed = failed.source
  const fallbackFailed = failed.fallback
  const [ratioW, ratioH] = ratio ?? (size ? [size, size] : [4, 3])
  const dimensions = { width: ratioW, height: ratioH }

  const targetWidth = thumbnailWidth ?? (size ? derivativeFor(size) : 768)
  const responsive = (source?: string) => {
    if (variantFailed) return { src: mediaUrl(source) }
    if (mediaUrl(source, targetWidth) === mediaUrl(source)) return { src: mediaUrl(source) }
    if (size || thumbnailWidth) {
      // A fixed slot names its 1x width; a phone screen paints it at 2–3x.
      // Without denser candidates a 48px avatar drew a 128px derivative at
      // 144 device pixels and a 4:3 kitchen card stretched 384 across ~700.
      const base = size ?? targetWidth
      const candidates = [`${mediaUrl(source, targetWidth)} 1x`]
      let previous: number = targetWidth
      for (const density of [2, 3]) {
        const width = derivativeFor(density * base)
        if (width > previous) candidates.push(`${mediaUrl(source, width)} ${density}x`)
        previous = width
      }
      return candidates.length > 1
        ? { src: mediaUrl(source, targetWidth), srcSet: candidates.join(', ') }
        : { src: mediaUrl(source, targetWidth) }
    }
    return {
      src: mediaUrl(source, 768),
      srcSet: `${mediaUrl(source, 384)} 384w, ${mediaUrl(source, 768)} 768w`,
      sizes: '(min-width: 1024px) 33vw, (min-width: 600px) 50vw, 100vw',
    }
  }
  const wrap = (node: ReactNode) =>
    size ? <SizedBox $size={size}>{node}</SizedBox> : <>{node}</>

  // Letterboxing is decided per source, once its pixels and slot are known.
  const [letterboxed, setLetterboxed] = useState<string | undefined>()
  const measure = (event: SyntheticEvent<HTMLImageElement>, key: string | undefined) => {
    const img = event.currentTarget
    if (!img.naturalWidth || !img.naturalHeight || img.clientWidth < LETTERBOX_MIN_SLOT || !img.clientHeight) return
    const slot = img.clientWidth / img.clientHeight
    const photo = img.naturalWidth / img.naturalHeight
    if (key && Math.max(slot / photo, photo / slot) > LETTERBOX_MISMATCH) setLetterboxed(key)
  }
  const photoNode = (Img: typeof ThumbImg, props: ComponentProps<typeof ThumbImg>) => {
    if (!props.src || letterboxed !== props.src) return <Img {...props} onLoad={(event) => measure(event, props.src)} />
    return (
      <LetterboxFrame>
        <LetterboxBackdrop src={props.src} srcSet={props.srcSet} sizes={props.sizes} alt="" aria-hidden="true" decoding="async" />
        <Img {...props} style={{ ...props.style, objectFit: 'contain', position: 'relative' }} />
      </LetterboxFrame>
    )
  }

  // Missing photos use illustrations. Stock substitutions require demo mode.
  const photo = isChef ? chefPhoto(src) : isSyntheticSeedAsset(src) ? undefined : src
  // A failed thumbnail first retries the original, then gives up to artwork.
  // Both URLs are derived from the same `photo` that was rendered.
  const onSourceError = () => {
    const original = mediaUrl(photo)
    if (!variantFailed && original && mediaUrl(photo, targetWidth) !== original) fail('variant')
    else fail('source')
  }

  if (isChef) {
    const resolved = mediaUrl(photo)
    if (resolved && !srcFailed) {
      return wrap(
        photoNode(ChefThumbImg, {
          ...responsive(photo),
          alt: alt || name || '',
          loading: 'lazy',
          decoding: 'async',
          width: dimensions.width,
          height: dimensions.height,
          style: objectPosition ? { objectPosition } : undefined,
          onError: onSourceError,
        }),
      )
    }
    return wrap(getChefIllustration(category, name || alt))
  }

  const resolved = mediaUrl(photo)
  if (resolved && !srcFailed) {
    return wrap(
      photoNode(ThumbImg, {
        ...responsive(photo),
        alt: alt || name || '',
        loading: 'lazy',
        decoding: 'async',
        width: dimensions.width,
        height: dimensions.height,
        style: objectPosition ? { objectPosition } : undefined,
        onError: onSourceError,
      }),
    )
  }

  if (category || name) {
    const curated = allowSeedContent() ? categoryImage(category, name || alt) : undefined
    if (curated && !fallbackFailed) {
      return wrap(
        <ThumbImg
          {...responsive(curated)}
          alt={alt || name || ''}
          loading="lazy"
          decoding="async"
          width={dimensions.width}
          height={dimensions.height}
          onError={() => fail(variantFailed ? 'fallback' : 'variant')}
        />,
      )
    }
    return wrap(isChef ? getChefIllustration(category, name || alt) : getDishIllustration(category, name || alt))
  }

  const emojiIllustration = resolveEmojiGlyph(emoji)
  if (emojiIllustration) return wrap(emojiIllustration)

  return wrap(<GlyphTile>{emoji ?? <BowlIcon size={24} />}</GlyphTile>)
}
